"use client";

import { useEffect, useRef, useState } from "react";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { ReviewCard } from "@/components/reviews/review-card";
import { surfaceFrom } from "@/components/optilase/surface";
import { resolveBg } from "@/lib/bg-color";
import type { GoogleReview } from "@/lib/google-reviews";

export type ReviewsGridBlok = SbBlokData & {
  title?: string;
  description?: string;
  view_more_label?: string;
  bg_color?: unknown;
  card_bg_color?: unknown;
};

const COLLAPSED_MAX_H_MOBILE = 1000;
const COLLAPSED_MAX_H_DESKTOP = 1700;

export default function ReviewsGridView({
  blok,
  reviews,
}: {
  blok: ReviewsGridBlok;
  reviews: GoogleReview[];
}) {
  const { bg, onDark, hex } = resolveBg(blok.bg_color, "white");
  const cardColor = surfaceFrom(blok.card_bg_color)?.backgroundColor ?? "#ecf9f9";
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(true);
  const gridRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const check = () => {
      const limit = window.innerWidth >= 1115 ? COLLAPSED_MAX_H_DESKTOP : COLLAPSED_MAX_H_MOBILE;
      setOverflows(el.offsetHeight > limit + 8);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    window.addEventListener("resize", check);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [reviews.length]);

  if (reviews.length === 0) return null;

  const collapsed = !expanded && overflows;

  return (
    <section {...storyblokEditable(blok)} className={`${bg} px-5 py-10 md:px-10 lg:px-20 lg:py-20`}>
      <div className="mx-auto w-full max-w-7xl">
        <div className="flex flex-col gap-4">
          {blok.title && (
            <h2 className={`text-section ${onDark ? "text-white" : "text-teal-07"}`}>
              {blok.title}
            </h2>
          )}
          {blok.description && (
            <p
              className={`max-w-[687px] text-base leading-normal lg:text-xl ${
                onDark ? "text-white/85" : "text-grey-06"
              }`}
            >
              {blok.description}
            </p>
          )}
        </div>

        <div className="relative mt-8 lg:mt-10">
          <div className={collapsed ? "max-h-250 overflow-hidden lg:max-h-425" : ""}>
            <ul
              ref={gridRef}
              className="columns-1 gap-6 sm:columns-2 [@media(min-width:1024px)]:columns-3"
            >
              {reviews.map((review, i) => (
                <li key={i} className="mb-6 break-inside-avoid">
                  <ReviewCard review={review} fullText cardColor={cardColor} />
                </li>
              ))}
            </ul>
          </div>
          {collapsed && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-48"
              style={{
                background: `linear-gradient(to top, ${hex}, transparent)`,
              }}
            />
          )}
        </div>

        {collapsed && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className={`inline-flex cursor-pointer items-center gap-1.5 font-bold hover:underline ${
                onDark ? "text-white" : "text-teal-07"
              }`}
            >
              {blok.view_more_label || "View more"}
              <span aria-hidden className="leading-none">
                +
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
